import { RushType } from '@/types/case.types';
import { FbsFileType, RecapFileType, RecapFrequency, StatementFileType } from '@/types/client.types';
import { AdvanceFilterOperator, EmailStatus } from '@/types/types';
import { Action, AfterAction, DepartmentType } from '@/types/workOrder.types';
import { z } from 'zod';

export const REPRESENTS = [
  { label: 'Plaintiff', value: 'PLAINTIFF' },
  { label: 'Defendant', value: 'DEFENDANT' },
  { label: 'Respondent', value: 'RESPONDENT' },
  { label: 'Petitioner', value: 'PETITIONER' },
  { label: 'Third Party Plaintiff', value: 'THIRD_PARTY_PLAINTIFF' },
  { label: 'Third Party Defendant', value: 'THIRD_PARTY_DEFENDANT' },
];

export const INACTIVITY_LIMIT = 1000 * 60 * 10;

export const WORK_ORDER_COMMENT_PAGE_SIZE = 100;

export const FAX_REGEX = /^\+[1-9][0-9\s\-().]{5,15}(?:\s?(?:ext|x|#)\s?\d{1,5})?$/i;

const BASIC_URL_REGEX = /^((http|https):\/\/)?(www\.)?(?!.*(http|https|www\.))([^\s/:?#]+(\.[^\s/:?#]+)+)(\/.*)?$/;

export const EMOJI_REGEX =
  /(?:[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E6}-\u{1F1FF}])/gu;

// BASIC_URL_REGEX is used to validate basic URL patterns. It matches URLs with optional protocols (http/https), an optional "www." prefix, and a valid domain name. The negative lookahead (?!.*(http|https|www\.)) ensures that redundant protocols or "www." prefixes do not appear elsewhere in the URL.
const isPortPresent = (url: URL): boolean => {
  return !!url.port;
};

export const invoiceStatusBadgeDetails = {
  PAID: {
    label: 'Paid',
    fontColor: '#1B8652',
    backgroundColor: '#D5F4E3',
  },
  REVIEWED: {
    label: 'Reviewed',
    fontColor: '#6C4A09',
    backgroundColor: '#FFEEBF',
  },
  CONFIRMED: {
    label: 'Confirmed',
    fontColor: '#FBEDFF',
    backgroundColor: '#551175',
  },
  DRAFT: {
    label: 'Draft',
    fontColor: '#0A2240',
    backgroundColor: '#C0CBD9',
  },
};

export const WEBSITE_VALIDATION = (error: string) =>
  z
    .string()
    .trim()
    .refine(
      (val) => {
        if (!BASIC_URL_REGEX.test(val)) return false;
        try {
          const url = new URL(val);
          if (isPortPresent(url)) return false;
          return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
          try {
            const url = new URL(`https://${val}`);
            if (isPortPresent(url)) return false;
            return url.protocol === 'https:';
          } catch {
            return false;
          }
        }
      },
      {
        message: error,
      }
    );

export const FAX_REPLACE_REGEX = /[^\d()+\-xX]|(?<!^)\+|^x|x(?=.*x)/gi;
export const SERVE_TYPE = [
  { label: 'Individual', value: 'INDIVIDUAL' },
  { label: 'Business', value: 'BUSINESS' },
];

export const SESSION_DISABLE_TIME = 1000 * 60 * 10;
export const SESSION_ENABLE_TIME = 1000 * 60 * 10;
export const CLIENT_CONTACT_STEP = 1;
export const AFFIDAVIT_TYPE = [
  {
    label: 'A8 - Standard Affidavit',
    value: 'A8',
    caseType: ['FORECLOSURE', 'LITIGATION', 'SUBPOENA'],
  },
  {
    label: 'A8 - Standard Affidavit (if applicable)',
    value: 'A8',
    caseType: ['PROPERTY_INSPECTION'],
  },
  {
    label: 'A8AA - Affidavit with an Attorney Acknowledgement',
    value: 'A8AA',
    caseType: ['FORECLOSURE', 'LITIGATION'],
  },
  { label: 'AG - Attorney General', value: 'AG', caseType: ['FORECLOSURE'] },
  { label: 'PR - Prosecutor', value: 'PR', caseType: ['FORECLOSURE'] },
  { label: 'US - United States Affidavit (Newark, NJ)', value: 'US', caseType: ['FORECLOSURE'] },
  { label: 'DC - United States Affidavit (Washington, DC)', value: 'DC', caseType: ['FORECLOSURE'] },
  { label: 'PO - Posting Affidavit', value: 'PO', caseType: ['FORECLOSURE', 'LITIGATION'] },
  { label: 'AM - Affidavit of Mailing', value: 'AM', caseType: ['FORECLOSURE', 'LITIGATION'] },
  {
    label: 'NY - NY Format Affidavit',
    value: 'NY',
    caseType: ['LITIGATION', 'NY_FORECLOSURE', 'NY_LANDLORD_TENANT', 'SUBPOENA'],
  },
  { label: 'PA - PA Format Affidavit', value: 'PA', caseType: ['LITIGATION', 'PA_FORECLOSURE', 'SUBPOENA'] },
  {
    label: 'A8VA - Vacant and Abandoned Inspection Affidavit (if necessary)',
    value: 'A8VA',
    caseType: ['VACANT_ABANDONED_INSPECTION'],
  },
];
export const ZIP_CODE_VALIDATION = {
  maxLength: 12,
  allowSpecialCharacters: true,
  allowNumber: true,
  replaceRegex: /[^0-9a-zA-Z\s-]/g,
};
export const SERVICE_TYPE = [
  { label: 'Process Service', value: 'PROCESS_SERVICE' },
  { label: 'Delivery', value: 'DELIVERY' },
  { label: 'Due Diligence Inquiry', value: 'DUE_DILIGENCE_INQUIRY' },
  { label: 'Home Inspection', value: 'HOME_INSPECTION' },
];
export const BILLING_TYPE = [
  {
    label: 'Invoice',
    value: 'INVOICE',
  },
  {
    label: 'Pre-paid',
    value: 'PREPAID',
  },
  {
    label: '0-Balance invoice',
    value: 'ZERO_BALANCE_INVOICE',
  },
];
export const ZIP_CODE_SCHEMA = z
  .string({ message: 'Zip code is required' })
  .min(1, 'Zip code is required')
  .regex(/^(?!.*[ -][ -])(?=(?:[^-]*-)?[^-]*$)(?=(?:[^\s]*\s)?[^\s]*$)[A-Za-z0-9](?:[A-Za-z0-9 -]*[A-Za-z0-9])?$/, {
    message: 'Zip code is invalid',
  })
  .refine((val) => val.replace(/[\s-]/g, '').length >= 3, { message: 'Zip code is invalid' })
  .refine((val) => val.replace(/[\s-]/g, '').length <= 10, { message: 'Zip code is invalid' });

export const NAME_VALIDATION = {
  maxLength: 35,
  capitalizeFirstLetter: true,
  allowNumber: false,
  replaceRegex: /^[^a-zA-Z]|(?!^)[^a-zA-Z\s.’'-]+/g,
  allowMoreThanTwoSameSpecialCharacters: false,
};
export const ADDRESS_LINE_VALIDATION = {
  maxLength: 100,
  allowSpecialCharacters: true,
  allowNumber: true,
};

export const PROPERTY_ADDRESS_CASE_TYPE: string[] = ['NY_FC', 'PA_FC', 'FC'];
export const CITY_VALIDATION = {
  maxLength: 50,
  capitalizeFirstLetter: true,
  allowSpecialCharacters: false,
  allowNumber: false,
};
export const EMAIL_ADDRESS_VALIDATION = {
  maxLength: 128,
};
export const STATE_VALIDATION = {
  maxLength: 2,
  capitalize: true,
  allowSpecialCharacters: false,
  allowNumber: false,
  replaceRegex: /\s+/g,
};

export const showFilterList = [
  { label: 'Search By Client', value: 'Client' },
  { label: 'Search by Invoice #', value: 'Invoice' },
  { label: 'Search by File #', value: 'File' },
];
export const Employee_type = [
  { label: 'Lawyer', value: 'LAWYER' },
  { label: 'Staff', value: 'STAFF' },
];
export const ROLE = [
  { label: 'Accountant', value: 'accountant' },
  { label: 'Admin', value: 'business' },
  { label: 'Operator', value: 'operator' },
  { label: 'Server', value: 'server' },
];
export const CONTACT_EMAIL_STATUS = [
  { label: 'Each Status', value: 'EACH' },
  { label: 'When Completed', value: 'COMPLETED' },
  { label: 'No Status', value: 'NONE' },
];
export const SENDAFFIDAVIT = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];
export const CASE_TYPE = [
  { label: 'Foreclosure', value: 'FORECLOSURE' },
  { label: 'Litigation', value: 'LITIGATION' },
  { label: 'NY Foreclosure', value: 'NY_FORECLOSURE' },
  { label: 'NY Landlord Tenant', value: 'NY_LANDLORD_TENANT' },
  { label: 'Other', value: 'OTHER' },
  { label: 'PA Foreclosure', value: 'PA_FORECLOSURE' },
  { label: 'Property Inspection', value: 'PROPERTY_INSPECTION' },
  { label: 'Subpoena', value: 'SUBPOENA' },
  { label: 'Vacant And Abandoned Inspection', value: 'VACANT_AND_ABANDONED_INSPECTION' },
];
export const CITIES = [
  { value: 'new-york', label: 'New York' },
  { value: 'los-angeles', label: 'Los Angeles' },
  { value: 'chicago', label: 'Chicago' },
  { value: 'houston', label: 'Houston' },
  { value: 'phoenix', label: 'Phoenix' },
  { value: 'philadelphia', label: 'Philadelphia' },
  { value: 'san-antonio', label: 'San Antonio' },
  { value: 'san-diego', label: 'San Diego' },
  { value: 'dallas', label: 'Dallas' },
  { value: 'san-jose', label: 'San Jose' },
  { value: 'austin', label: 'Austin' },
  { value: 'jacksonville', label: 'Jacksonville' },
  { value: 'fort-worth', label: 'Fort Worth' },
  { value: 'columbus', label: 'Columbus' },
  { value: 'charlotte', label: 'Charlotte' },
  { value: 'san-francisco', label: 'San Francisco' },
  { value: 'indianapolis', label: 'Indianapolis' },
  { value: 'seattle', label: 'Seattle' },
  { value: 'denver', label: 'Denver' },
];

export const COURTS = [
  { label: 'LUNN - Superior Court of New Jersey', value: 'LUNN - Superior Court of New Jersey', code: 'LUNN' },
  { label: 'Kings County Supreme Court', value: 'Kings County Supreme Court', code: 'Kings' },
  { label: 'Queens County Civil Court', value: 'Queens County Civil Court', code: 'Queens' },
  { label: 'Brooklyn Family Court', value: 'Brooklyn Family Court', code: 'Brooklyn' },
  { label: 'Nassau County Court', value: 'Nassau County Court', code: 'Nassau' },
  { label: "Richmond County Surrogate's Court", value: "Richmond County Surrogate's Court", code: 'Richmond' },
  { label: 'Bronx County Criminal Court', value: 'Bronx County Criminal Court', code: 'Bronx' },
  { label: 'Westchester County Supreme Court', value: 'Westchester County Supreme Court', code: 'Westchester' },
  { label: 'Suffolk County District Court', value: 'Suffolk County District Court', code: 'Suffolk' },
  { label: 'Orange County Family Court', value: 'Orange County Family Court', code: 'Orange' },
];

export const CLIENTOPTIONS = [
  { label: 'LUNN - Superior Court of New Jersey', value: 'LUNN' },
  { label: 'Kings County Supreme Court', value: 'Kings' },
  { label: 'Queens County Civil Court', value: 'Queens' },
  { label: 'Brooklyn Family Court', value: 'Brooklyn' },
  { label: 'Nassau County Court', value: 'Nassau' },
  { label: "Richmond County Surrogate's Court", value: 'Richmond' },
  { label: 'Bronx County Criminal Court', value: 'Bronx' },
  { label: 'Westchester County Supreme Court', value: 'Westchester' },
  { label: 'Suffolk County District Court', value: 'Suffolk' },
  { label: 'Orange County Family Court', value: 'Orange' },
];

export const RACE_OPTIONS = [
  {
    label: 'Caucasian',
    value: 'Caucasian',
  },
  {
    label: 'Black',
    value: 'Black',
  },
  {
    label: 'Hispanic',
    value: 'Hispanic',
  },
  {
    label: 'Middle Eastern',
    value: 'Middle Eastern',
  },
  {
    label: 'Asian',
    value: 'Asian',
  },
  {
    label: 'Indian',
    value: 'Indian',
  },
];

export const JURISDICTION_OPTIONS = [
  {
    label: 'Federal',
    value: 'FEDERAL',
  },
  {
    label: 'State',
    value: 'STATE',
  },
];

export const HEARING_DATE_CASE_TYPE = {
  SUB: 'SUB',
  NY_LT: 'NY_LT',
};

export enum Feature {
  DASHBOARD = 'dashboard',
  USERS = 'user',
  CLIENTS = 'client',
  COURTS = 'court',
  SERVICES = 'service',
  BILLING = 'billing',
  BILLING_ENTRY = 'billing_entry',
  BILLING_SCREENS = 'billing_screens',
  STATUS_REPORT = 'status_report',
  VENDORS = 'vendor',
  DELIVERY = 'delivery',
  DELIVERY_UPDATES = 'delivery.deliveryUpdates',
  DELIVERY_UPDATES_READ = 'delivery.deliveryUpdates.read',
  ACCOUNTS = 'accounting',
  ACCOUNTS_RECEIVABLE = 'accounting.accountsReceivable',
  CHECK_REGISTER = 'accounting.checkRegister',
  STATEMENTS = 'statement.read',
  VENDOR_PAYMENT = 'accounting.vendor.read',
  VENDOR_PAYMENT_APPLY = 'accounting.vendor.payment',
  PAYMENT_HISTORY = 'accounting.paymentHistroy.read',
  PAYMENT_HISTORY_EXPORT = 'accounting.paymentHistroy.export',
  RECAPS = 'recap.read',
  RECAP_SHARE = 'recap.share',
  FBS = 'fbs.read',
  FBS_GENERATE = 'fbs.generate',
  FBS_EXPORT = 'fbs.export',
  FBS_SHARE = 'fbs.share',
  FBS_APPLY_PAYMENT = 'fbs.applyPayment',
  CLIENT_BILLING_PREFERENCES = 'client.billingPreference',
  PROCESS_BILLING_SERVER = 'accounting.processBilling.server',
  PROCESS_BILLING_VENDOR = 'accounting.processBilling.vendor',
  HOME_INSPECTION = 'homeInspection',
  HOME_INSPECTION_WORK_ORDER_CREATE = 'homeInspection.workOrder.create',
  HOME_INSPECTION_WORK_ORDER_UPDATE = 'homeInspection.workOrder.update',
  HOME_INSPECTION_EXPORT_HIOR_PDF = 'homeInspection.export.HIORPDF',
  HOME_INSPECTION_WORK_ORDER_COMMENTS = 'homeInspection.workOrder.comments',
  /** Gates HI Email Attempt Updates UI; matches API flag `homeInspection.shareUpdates`. */
  HOME_INSPECTION_SHARE_UPDATES = 'homeInspection.shareUpdates',
  HOME_INSPECTION_EDIT_STATUS = 'homeInspection.edit.status',
  DUE_DILIGENCE = 'dueDiligence',
  DUE_DILIGENCE_WORK_ORDER_READ = 'dueDiligence.workOrder.read',
  DUE_DILIGENCE_WORK_ORDER_CREATE = 'dueDiligence.workOrder.create',
  DUE_DILIGENCE_WORK_ORDER_EDIT = 'dueDiligence.workOrder.edit',
  DUE_DILIGENCE_WORK_ORDER_ASSIGN = 'dueDiligence.workOrder.assign',
  DUE_DILIGENCE_WORK_ORDER_UPDATE_STATUS = 'dueDiligence.workOrder.updateStatus',
  DUE_DILIGENCE_WORK_ORDER_COMMENTS = 'dueDiligence.workOrder.comments',
  DUE_DILIGENCE_INVOICE = 'dueDiligence.invoice',
  DUE_DILIGENCE_INVOICE_SHARE = 'dueDiligence.invoice.share',
  DUE_DILIGENCE_INVOICE_EXPORT = 'dueDiligence.invoice.export',
  DUE_DILIGENCE_SEARCH = 'dueDiligenceSearch',
}

export const WORK_ORDER_STATUS_OPTIONS = [
  { label: 'Assigned', value: 'ASSIGNED' },
  { label: 'Awaiting Client', value: 'AWAITING' },
  { label: 'Canceled', value: 'CANCELED' },
];

export const DELIVERY_STATUS_OPTIONS = [
  { label: 'Draft', value: 'Draft' },
  { label: 'Converted', value: 'Converted' },
  { label: 'Unassigned', value: 'Unassigned' },
  { label: 'Assigned', value: 'Assigned' },
  { label: 'Out for Delivery', value: 'OutForDelivery' },
  { label: 'Delivered', value: 'Delivered' },
  { label: 'Billed', value: 'Billed' },
  { label: 'Re-scheduled', value: 'Rescheduled' },
  { label: 'Canceled', value: 'Cancelled' },
];

export const HOME_INSPECTION_STATUS_OPTIONS = [
  { label: 'Awaiting Client', value: 'AWAITING_CLIENT' },
  { label: 'Assigned', value: 'ASSIGNED' },
  { label: 'Canceled', value: 'CANCELED' },
];

export const DDI_WORK_ORDER_STATUS_OPTIONS = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Pending Results', value: 'PENDING_RESULTS' },
  { label: 'Assigned', value: 'ASSIGNED' },
  { label: 'Awaiting Client', value: 'AWAITING_CLIENT' },
  { label: 'Review & Bill', value: 'REVIEW_AND_BILL' },
  { label: 'Billed', value: 'BILLED' },
  { label: 'Canceled', value: 'CANCELED' },
];

export const SSN_SEARCH_DDI_TYPE = 'SSN search';

export const DDI_RUSH_FILTER_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Rush', value: 'RUSH' },
];

export const DDI_RUSH_TYPE_OPTIONS = [
  { label: 'Rush', value: 'RUSH' },
  { label: 'Same day', value: 'SAME_DAY' },
  { label: 'Next day', value: 'NEXT_DAY' },
];

export const HOME_INSPECTION_RUSH_FILTER_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Rush', value: 'RUSH' },
];

export const US_STATES_OPTIONS = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'District of Columbia', value: 'DC' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

export const FILTER_OPERATOR_OPTIONS = [
  {
    label: 'Greater Than (>)',
    value: AdvanceFilterOperator.GREATER_THAN,
  },
  {
    label: 'Greater Than or Equal to (>=)',
    value: AdvanceFilterOperator.GREATER_THAN_OR_EQUAL_TO,
  },
  {
    label: 'Less Than (<)',
    value: AdvanceFilterOperator.LESS_THAN,
  },
  {
    label: 'Less Than or Equal to (<=)',
    value: AdvanceFilterOperator.LESS_THAN_OR_EQUAL_TO,
  },
  {
    label: 'Equal To (=)',
    value: AdvanceFilterOperator.EQUALS,
  },
  {
    label: 'Contains',
    value: AdvanceFilterOperator.CONTAINS,
  },
];

export const WorkOrderVendorDepartmentOptions = [
  {
    label: 'National',
    value: DepartmentType.NATIONAL,
  },
  {
    label: 'International',
    value: DepartmentType.INTERNATIONAL,
  },
  {
    label: 'Deputization',
    value: DepartmentType.DEPUTIZATION,
  },
  {
    label: 'UIDDA',
    value: DepartmentType.UIDDA,
  },
];

export const WorkOrderVendorActionOptions = [
  {
    label: 'Serve',
    value: Action.SERVE,
  },
  {
    label: 'Post',
    value: Action.POST,
  },
  {
    label: 'Issue Then Serve',
    value: Action.ISSUE_THEN_SERVE,
  },
  {
    label: 'Other',
    value: Action.OTHER,
  },
];

export const WorkOrderVendorAfterActionOptions = [
  {
    label: 'Notarize Affidavit',
    value: AfterAction.NOTARIZE_AFFIDAVIT,
  },
  {
    label: 'Use Our Proof Service',
    value: AfterAction.USE_PROOF_SERVICE,
  },
  {
    label: 'File Affidavit',
    value: AfterAction.FILE_AFFIDAVIT,
  },
  {
    label: 'Need Original Affidavit',
    value: AfterAction.NEED_ORIGINAL_AFFIDAVIT,
  },
  {
    label: 'Other',
    value: AfterAction.OTHER,
  },
];

export const RUSH_TYPE_OPTIONS = [
  {
    label: 'Same day',
    value: RushType.SAME_DAY,
  },
  {
    label: 'Next day',
    value: RushType.NEXT_DAY,
  },
];

export const EmailLogStatusLabel = {
  [EmailStatus.SENT]: 'Sent',
  [EmailStatus.FAILED]: 'Failed to Send',
};

export const caseTypesWithWitnessFees = ['SUB'];

export const APPLICATION_NAME = 'dgr-ui';

export const RECAP_FREQUENCY = [
  {
    label: 'Weekly',
    value: RecapFrequency.WEEKLY,
  },
  {
    label: 'Nightly',
    value: RecapFrequency.NIGHTLY,
  },
];

export const FBS_FILE_TYPE = [
  { label: 'PDF', value: FbsFileType.PDF },
  { label: 'CSV', value: FbsFileType.CSV },
  { label: 'Both', value: FbsFileType.BOTH },
];

export const RECAP_FILE_TYPE = [
  {
    label: 'PDF',
    value: RecapFileType.PDF,
  },
  {
    label: 'CSV',
    value: RecapFileType.CSV,
  },
  {
    label: 'Both',
    value: RecapFileType.BOTH,
  },
];

export const STATEMENT_FILE_TYPE = [
  {
    label: 'CSV',
    value: StatementFileType.CSV,
  },
  {
    label: 'PDF',
    value: StatementFileType.PDF,
  },
  {
    label: 'Both',
    value: StatementFileType.BOTH,
  },
];

export const FINANCIAL_CHARGES_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];
